import { useEffect, useState , useCallback} from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  Calendar,
  MapPin,
  User as UserIcon,
  Clock,
} from "lucide-react";

/**
 * AdminEventsQueueTab
 * Reviews user-submitted events (POST /api/events/submit → status="pending").
 * Tabs: Pending / Published / Rejected.
 * Approve → status flips to "published", is_published=true, visible on /events.
 * Reject → status flips to "rejected", hidden.
 */
export default function AdminEventsQueueTab() {
  const [status, setStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

    const fetchQueue = useCallback(async (which = status) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/events/queue`, {
        params: { status: which },
        withCredentials: true,
      });
      setRows(r.data?.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load events queue");
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    fetchQueue(status);
  }, [fetchQueue, status]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/events/approve/${id}`, {}, { withCredentials: true });
      toast.success("Approved — now live on /events");
      setRows((rs) => rs.filter((r) => r.event_id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approve failed");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/events/reject/${id}`, {}, { withCredentials: true });
      toast.success("Rejected");
      setRows((rs) => rs.filter((r) => r.event_id !== id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reject failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b">
        <h2 className="font-heading text-xl text-tbc-navy flex items-center gap-2">
          <Calendar className="w-5 h-5 text-tbc-gold" />
          Events Queue
        </h2>
        <p className="text-sm text-gray-500 font-body mt-1">
          Community-submitted events awaiting review. Approve to publish on{" "}
          <span className="font-medium">/events</span>.
        </p>
      </div>

      <Tabs value={status} onValueChange={setStatus} className="w-full">
        <div className="px-6 pt-4">
          <TabsList>
            <TabsTrigger value="pending" data-testid="events-tab-pending">Pending</TabsTrigger>
            <TabsTrigger value="published" data-testid="events-tab-published">Published</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="events-tab-rejected">Rejected</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={status} className="p-6">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-gray-500 font-body">
              {status === "pending"
                ? "No pending submissions yet."
                : `No ${status} events yet.`}
            </div>
          ) : (
            <div className="space-y-4">
              {rows.map((ev) => (
                <EventRow
                  key={ev.event_id}
                  event={ev}
                  status={status}
                  busy={busyId === ev.event_id}
                  onApprove={() => approve(ev.event_id)}
                  onReject={() => reject(ev.event_id)}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function EventRow({ event: ev, status, busy, onApprove, onReject }) {
  return (
    <div
      className="flex flex-col md:flex-row gap-4 p-4 border border-gray-100 rounded-xl hover:bg-tbc-cream/30 transition-colors"
      data-testid={`event-row-${ev.event_id}`}
    >
      {ev.image ? (
        <img
          src={ev.image}
          alt={ev.title}
          className="w-full md:w-40 h-24 rounded-lg object-cover bg-gray-100 flex-shrink-0"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <div className="w-full md:w-40 h-24 rounded-lg bg-tbc-cream flex items-center justify-center flex-shrink-0">
          <Calendar className="w-8 h-8 text-tbc-gold/60" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-widest text-tbc-gold border border-tbc-gold/40 px-2 py-0.5 rounded-full font-body">
            {ev.category || "Event"}
          </span>
          {ev.price && (
            <span className="text-xs bg-tbc-navy/10 text-tbc-navy px-2 py-0.5 rounded font-body">
              {ev.price}
            </span>
          )}
        </div>
        <h3 className="font-heading text-tbc-navy text-base leading-snug">
          {ev.title}
        </h3>
        {ev.description && (
          <p className="text-sm text-gray-600 font-body mt-1 line-clamp-2 leading-relaxed">
            {ev.description}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500 font-body">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" /> {ev.date}
          </span>
          {ev.time && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" /> {ev.time}
            </span>
          )}
          {ev.location && (
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {ev.location}
              {ev.venue ? ` · ${ev.venue}` : ""}
            </span>
          )}
          {ev.submitted_by_email && (
            <span className="flex items-center gap-1">
              <UserIcon className="w-3 h-3" /> {ev.submitted_by_email}
            </span>
          )}
        </div>
      </div>

      <div className="flex md:flex-col gap-2 md:justify-center">
        {status !== "published" && (
          <Button
            size="sm"
            disabled={busy}
            onClick={onApprove}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`events-approve-${ev.event_id}`}
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (
              <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</>
            )}
          </Button>
        )}
        {status !== "rejected" && (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onReject}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid={`events-reject-${ev.event_id}`}
          >
            <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
          </Button>
        )}
      </div>
    </div>
  );
}
